<template>
  <CollapsableSection
    ref="container"
    :class="$style.collapsableSection"
    :collapse="false"
    title="Event Frequency"
    @opened="updateChartWidth"
  >
    <div
      ref="chartContainer"
      :class="$style.chartContainer"
    >
      <svg
        v-show="!error && !loading && eventInTimeRange"
        :data-test-id="$testId('svg')"
        :height="height + margin.top + margin.bottom"
        :width="computedWidth"
      >
        <g :transform="`translate(${margin.left}, ${margin.top})`">
          <g
            v-if="x && y"
            ref="chart"
          >
            <g
              v-for="(d, index) in intervalData"
              :key="d.timestamp"
              :class="$style.barGroup"
              :transform="`translate(${x(d.timestamp)}, 0)`"
            >
              <rect
                :class="$style.bar"
                :height="y(0) - y(d.value)"
                :width="x.bandwidth()"
                :y="y(d.value)"
                @click="handleBarClick(d)"
                @mouseenter="e => showTooltip(e.target, d, index)"
                @mouseleave="hideTooltip"
              />
            </g>
          </g>
          <g>
            <g
              ref="xAxis"
              :transform="`translate(0, ${height})`"
            />
            <g ref="yAxis" />
            <text
              :class="$style.axisLabel"
              :x="width / 2"
              :y="height + 38"
            >
              Time Range
              {{ timeLabelText }}
            </text>
            <text
              :class="$style.axisLabel"
              :y="-12"
            >
              # of Events
            </text>
          </g>
        </g>
      </svg>
      <div
        v-show="!error && !loading && !eventInTimeRange"
        :class="$style.noDataMessage"
      >
        No chart data to display
      </div>
      <LoadingIcon
        v-show="loading"
        :class="$style.loadingIcon"
      />
      <div
        v-show="error"
        :class="$style.errorMessage"
      >
        There was an error retrieving chart data.
      </div>
      <div
        v-show="tooltip.visible"
        :class="[$style.tooltip, {
          [$style.tooltipDisplayLeft]: tooltip.displayOnLeftSide,
        }]"
        :style="tooltip.style"
      >
        <div
          v-for="{ label, value } in tooltip.content"
          :key="label"
          :class="$style.tooltipRow"
        >
          <label :class="$style.tooltipLabel">
            {{ label }}
          </label>
          <span :class="$style.tooltipValue">
            {{ value }}
          </span>
        </div>
      </div>
    </div>
  </CollapsableSection>
</template>

<script>
import * as d3 from 'd3';
import { ItemTypeConsts, StoreModuleMap } from 'client/util/Constants';
import { get } from 'lodash';
import { mapActions, mapState } from 'vuex';
import CollapsableSection from 'components/ContentSection/CollapsableSection.vue';
import LoadingIcon from 'components/LoadingOverlay/LoadingIcon.vue';
import moment from 'moment';

const insightsEventModule = StoreModuleMap[ItemTypeConsts.insightsEvent];
const timeUnit = {
  second: 's',
  minute: 'm',
  hour: 'h',
  day: 'd',
  week: 'w',
};

export default {
  name: 'ActivityLogChart',

  components: {
    CollapsableSection,
    LoadingIcon,
  },

  props: {
    searchQuery: {
      type: String,
      default: () => '',
    },
  },

  data: () => ({
    error: false,
    height: 90,
    intervalData: [],
    intervalUnit: timeUnit.hour,
    intervalValue: 1,
    lastWindowWidth: null,
    loading: false,
    margin: {
      bottom: 50,
      left: 45,
      right: 10,
      top: 45,
    },
    timeLabelText: '',
    tooltip: {
      displayOnLeftSide: false,
      style: {},
      visible: false,
    },
    width: 640,
    x: null,
    y: null,
  }),

  computed: {
    ...mapState(insightsEventModule, [
      'endDate',
      'filters',
      'startDate',
    ]),

    computedWidth() {
      return this.width + this.margin.left + this.margin.right;
    },

    eventInTimeRange() {
      return this.intervalData.some(d => d.value);
    },
  },

  watch: {
    endDate() { this.updateChartDebounce(); },
    filters: {
      handler() { this.updateChartDebounce(); },
      deep: true,
    },
    startDate() { this.updateChartDebounce(); },
  },

  destroyed() {
    window.removeEventListener('resize', this.handleWindowResize);
  },

  mounted() {
    this.updateChart();
    setTimeout(() => {
      this.updateChartWidth();
    }, 0);
    window.addEventListener('resize', this.handleWindowResize);
  },

  methods: {
    ...mapActions(insightsEventModule, [
      'fetchEventsIntervalCount',
      'setEndDate',
      'setQuickPickDate',
      'setStartDate',
    ]),

    fetchChartData() {
      this.setInterval();
      this.error = false;
      this.loading = true;
      return this.fetchEventsIntervalCount({
        interval_unit: this.intervalUnit,
        interval_value: String(this.intervalValue),
        q: this.searchQuery,
      })
        .then((resp) => {
          // Set data
          const buckets = get(resp, 'data.buckets', []);
          return buckets.map(d => ({
            timestamp: d.key_as_string,
            value: d.doc_count,
          }));
        })
        .catch(() => {
          this.error = true;
          return [];
        })
        .finally(() => {
          this.loading = false;
        });
    },

    formatTimestamp(timestamp) {
      // Format x-axis labels
      if (this.intervalUnit === timeUnit.week || this.intervalUnit === timeUnit.day) {
        return moment(timestamp).format('MMM DD');
      }
      if (this.intervalUnit === timeUnit.hour || this.intervalUnit === timeUnit.minute) {
        return moment(timestamp).format('HH:mm');
      }
      return moment(timestamp).format('HH:mm:ss');
    },

    handleBarClick(d) {
      if (this.intervalUnit !== timeUnit.second) {
        this.setStartDate(new Date(d.timestamp));
        const endDate = moment(d.timestamp).add(this.intervalValue, this.intervalUnit).toDate();
        this.setEndDate(endDate);
        this.setQuickPickDate('');
        this.$emit('datechange');
      }
    },

    handleWindowResize() {
      clearTimeout(this.windowResizeTimeout);
      const windowWidth = window.innerWidth;

      // Lengthen timeout if window crosses 'max-width-large' breakpoint
      // Fixes chart resize issue affected by NavPanel expand & collapse
      const debounceVal = (windowWidth >= 1024 && this.lastWindowWidth < 1024)
      || (windowWidth <= 1024 && this.lastWindowWidth > 1024) ? 250 : 100;

      this.lastWindowWidth = window.innerWidth;

      this.windowResizeTimeout = setTimeout(() => {
        this.updateChartWidth();
        this.updateChartElements();
      }, debounceVal);
    },

    hideTooltip() {
      this.tooltip.visible = false;
    },

    setInterval() {
      // Determine interval unit to request data for based on time range
      let numDataPoints = 1;
      const numSeconds = (this.endDate - this.startDate) / 1000;
      const numMinutes = numSeconds / 60;
      const numHours = numMinutes / 60;
      const numDays = numHours / 24;
      const numWeeks = numDays / 7;
      if (numMinutes < 5) {
        this.intervalUnit = timeUnit.second; // display in seconds if under 5 min
        numDataPoints = numSeconds;
      } else if (numHours < 5) {
        this.intervalUnit = timeUnit.minute; // display in minutes if under 5 hours
        numDataPoints = numMinutes;
      } else if (numDays < 5) {
        this.intervalUnit = timeUnit.hour; // display in hours if under 5 days
        numDataPoints = numHours;
      } else if (numWeeks < 5) {
        this.intervalUnit = timeUnit.day; // display in days if under 5 weeks
        numDataPoints = numDays;
      } else {
        this.intervalUnit = timeUnit.week;
        numDataPoints = numWeeks;
      }

      // Adjust interval value to limit number of data points (max 15)
      this.intervalValue = Math.ceil(numDataPoints / 15);
    },

    setTimeLabelText() {
      // Set the extra text shown below the x-axis to indicate time range
      const timeIntervalUnitText = {
        s: 'Second',
        m: 'Minute',
        h: 'Hour',
        d: 'Day',
        w: 'Week',
      }[this.intervalUnit];
      if (this.intervalValue === 1) {
        this.timeLabelText = ` by ${timeIntervalUnitText}`;
      } else {
        this.timeLabelText = ` by ${this.intervalValue} ${timeIntervalUnitText}s`;
      }
      if (this.intervalUnit === timeUnit.second
        || this.intervalUnit === timeUnit.minute
        || this.intervalUnit === timeUnit.hour) {
        this.timeLabelText += ` (${moment(this.startDate).format('MMM DD, YYYY')})`;
      }
    },

    showTooltip(target, datum, index) {
      // Set tooltip position; the (8, -27) offset is to align the tooltip arrow with the bar
      const barRect = target.getBoundingClientRect();
      const containerRect = this.$refs.chartContainer.getBoundingClientRect();
      this.tooltip.displayOnLeftSide = (index > this.intervalData.length / 2);
      if (this.tooltip.displayOnLeftSide) {
        // Show tooltip on left side of bar
        this.$set(this.tooltip.style, 'left', 'auto');
        this.$set(this.tooltip.style, 'right', `${containerRect.x + containerRect.width - barRect.x + 8}px`);
      } else {
        // Show tooltip on right side of bar
        this.$set(this.tooltip.style, 'left', `${barRect.x + barRect.width - containerRect.x + 8}px`);
        this.$set(this.tooltip.style, 'right', 'auto');
      }
      this.$set(this.tooltip.style, 'top', `${barRect.y + (barRect.height / 2) - containerRect.y - 27}px`);

      // Set tooltip content
      const startDate = moment(datum.timestamp);
      const endDate = moment(datum.timestamp).add(this.intervalValue, this.intervalUnit);
      const timeFormat = 'MMM DD, YYYY HH:mm:ss';
      this.$set(this.tooltip, 'content', [
        { label: 'Start Time', value: startDate.format(timeFormat) },
        { label: 'End Time', value: endDate.format(timeFormat) },
        { label: 'Event Count', value: datum.value.toLocaleString() },
      ]);

      this.tooltip.visible = true;
    },

    async updateChart() {
      const data = await this.fetchChartData();
      this.updateChartElements(data);
    },

    updateChartDebounce() {
      clearTimeout(this.updateChartTimeout);
      this.updateChartTimeout = setTimeout(() => {
        this.updateChart();
      }, 50);
    },

    /**
     * Update the scales based on data. This will update reactive chart elements.
     * @param {Array} [data] - An optional argument. If provided, uses that data and stores it
     */
    updateChartElements(data) {
      const intervalData = data || this.intervalData;
      if (!intervalData) return;

      // Establish x band scale based on data points (1 data point = 1 bar)
      this.x = d3.scaleBand()
        .padding(0.2) // sets space between bars at 20% of bar width
        .domain(intervalData.map(d => d.timestamp))
        .range([0, this.width]);

      // Establish linear y scale based on max data value (minimum 5)
      const maxValue = d3.max(intervalData, d => d.value) * 1.2;
      this.y = d3.scaleLinear()
        .domain([0, d3.max([maxValue, 5])])
        .range([this.height, 0]);

      // Update axes to reflect scales
      const xAxis = d3.axisBottom()
        .tickFormat(this.formatTimestamp)
        .tickSizeInner(4) // sets tick size in px
        .scale(this.x);
      const yAxis = d3.axisLeft()
        .tickSizeInner(4) // sets tick size in px
        .tickSizeOuter(0)
        .ticks(5) // sets recommended number of ticks (d3 doesn't strictly follow this)
        .scale(this.y);
      d3.select(this.$refs.xAxis).call(xAxis);
      d3.select(this.$refs.yAxis).call(yAxis);

      this.setTimeLabelText();

      // Bind data to component last so HTML elements will update after scales are defined
      if (data) this.intervalData = data;
    },

    updateChartWidth() {
      const containerElement = this.$refs.container.$el;
      // subtract padding from width
      const containerStyles = window.getComputedStyle(containerElement);
      const containerWidth = containerElement.clientWidth
        - parseFloat(containerStyles.paddingLeft)
        - parseFloat(containerStyles.paddingRight);

      if (containerWidth >= 0) {
        this.width = containerWidth - this.margin.left - this.margin.right;
      }
    },
  },
};
</script>

<style module>
@value (
  gradientDark2,
  gradientDark4,
  gradientLight6
) from '~css/colors.css';
@value lightShadow1 from '~css/shadows.css';

.collapsableSection.collapsableSection {
  border: 1px solid gradientLight6;
  border-radius: 6px;
  overflow: visible;
  padding: 12px 40px;
}

.collapsableSection > div { padding: 0; } /* targeting sectionContent of CollapsableSection */

.collapsableSection > h2 > div {
  /* targeting header of CollapsableSection */
  font-size: 14px !important;
  position: relative;
  top: 1px;
}

.chartContainer {
  position: relative;
  text-align: center;
}

.bar {
  cursor: pointer;
  fill: var(--jcPrimary);
}

.barGroup { font-size: 12px; }
.barGroup:hover .bar { fill: var(--jcPrimary700); }

.axisLabel {
  font-size: 12px;
  font-weight: 600;
  text-anchor: middle;
}

.errorMessage,
.loadingIcon,
.noDataMessage {
  font-style: italic;
  margin: 30px 50px;
}

.tooltip {
  background-color: gradientDark2;
  border-radius: 2px;
  box-shadow: lightShadow1;
  color: white;
  font-size: 12px;
  padding: 10px 20px;
  position: absolute;
  text-align: left;
}

.tooltip::after {
  border-bottom: 7px solid transparent;
  border-right: 7px solid gradientDark2;
  border-top: 7px solid transparent;
  content: '';
  height: 0;
  left: -7px;
  position: absolute;
  top: 20px;
  width: 0;
}

.tooltipDisplayLeft::after {
  border-left: 7px solid gradientDark2;
  border-right: none;
  left: auto;
  right: -7px;
}

.tooltipRow:nth-child(n+2) {
  margin-top: 10px;
}

.tooltipLabel {
  color: #aaa;
  margin-bottom: 0 !important; /* overriding legacy (.jc-ux2 > label) rule */
}

.tooltipValue {
  display: block;
  font-weight: 600;
}
</style>
